import React, { useRef, useState, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { AspectRatio, MediaQuery, generateImageRenditions } from '../../utils/ImageUtils';
import type { ResponsiveImageSpec } from '../../utils/ImageUtils';
import { AImage } from '../../atoms/AImage/index';
import { MButtonOverlay } from '../MButtonOverlay/index';
import { AIconSize } from '../../atoms/AIcon/AIcon.types';
import * as Styled from './MVideoThumbnail.styles';
import {
  type MVideoThumbnailProps,
  MVideoThumbnailAriaLabel,
  MVideoThumbnailErrorMessage,
} from './MVideoThumbnail.types';
import { OModalMedia } from '../../organisms/OModalMedia';
import { OModalMediaType } from '../../organisms/OModalMedia/OModalMedia.types';
import type { OModalMediaProps } from '../../organisms/OModalMedia/OModalMedia.types';
import type { MButtonOverlayProps } from '../MButtonOverlay/MButtonOverlay.types';

const DEFAULT_BUTTON_OVERLAY: MButtonOverlayProps = {
  iconOnly: true,
  aIcon: { icon: 'icon-play', size: AIconSize.L },
  imageOverlay: true,
};

const MVIDEOTHUMBNAIL_IMAGE_RENDITION_SPECS: ResponsiveImageSpec[] = [
  { media: MediaQuery.MAX_WIDTH_SM, aspectRatio: AspectRatio.SQUARE, imgWidthPx: 540 },
  { media: MediaQuery.MIN_WIDTH_MD, aspectRatio: AspectRatio.CLASSIC_HOR, imgWidthPx: 768 },
  { media: MediaQuery.MIN_WIDTH_LG, aspectRatio: AspectRatio.CLASSIC_HOR, imgWidthPx: 1200 },
  { media: MediaQuery.MIN_WIDTH_XL, aspectRatio: AspectRatio.CLASSIC_HOR, imgWidthPx: 1600 },
];

const COMPONENT_NAME = 'm-web-ui-kit-MVideoThumbnail';
const TEST_ID = 'web-ui-kit-MVideoThumbnail';

export const MVideoThumbnail: React.FC<MVideoThumbnailProps> = ({
  aImage,
  aVideo,
  oModalMedia,
  onModalOpen,
  mButtonOverlay,
  renditionSpecs,
  loading,
  className,
  ...nativeProps
}) => {
  const [hasImageError, setHasImageError] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleImageError = useCallback(() => setHasImageError(true), []);

  const specs = renditionSpecs ?? MVIDEOTHUMBNAIL_IMAGE_RENDITION_SPECS;
  const renditions = aImage?.image?.assetPath ? generateImageRenditions(specs, aImage.image.assetPath) : undefined;

  const resolvedModalMedia = useMemo<Partial<OModalMediaProps> | undefined>(() => {
    const base = oModalMedia ?? (aVideo ? { type: OModalMediaType.Video, aVideo } : undefined);
    if (!base) return undefined;
    return { ...base, triggerRef: buttonRef };
  }, [oModalMedia, aVideo]);

  const handlePlay = useCallback(() => {
    if (!resolvedModalMedia) return;
    setIsModalOpen(true);
    onModalOpen?.(resolvedModalMedia);
  }, [resolvedModalMedia, onModalOpen]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const ariaLabel = aImage?.image?.altText ?? aImage?.alt ?? MVideoThumbnailAriaLabel.DefaultPlay;

  return (
    <>
      <Styled.Wrapper
        ref={buttonRef}
        {...nativeProps}
        $noImage={!aImage}
        data-component-name={COMPONENT_NAME}
        data-testid={TEST_ID}
        className={clsx('inverse', className)}
      >
        {aImage && (
          <Styled.ThumbnailImage>
            <AImage {...aImage} renditions={renditions} loading={loading ?? 'lazy'} onError={handleImageError} />
          </Styled.ThumbnailImage>
        )}

        <Styled.OverlayContainer>
          <MButtonOverlay
            imageOverlay={true}
            {...(mButtonOverlay ?? DEFAULT_BUTTON_OVERLAY)}
            iconOnly={true}
            aria-label={ariaLabel}
            disabled={!resolvedModalMedia}
            onClick={resolvedModalMedia ? handlePlay : undefined}
          />
        </Styled.OverlayContainer>

        {aImage && hasImageError && (
          <Styled.ImageErrorOverlay role="alert" aria-live="assertive" aria-atomic="true">
            <Styled.ErrorText>{MVideoThumbnailErrorMessage.ThumbnailFailed}</Styled.ErrorText>
            {ariaLabel && <Styled.ErrorText>{ariaLabel}</Styled.ErrorText>}
          </Styled.ImageErrorOverlay>
        )}
      </Styled.Wrapper>
      {resolvedModalMedia && (
        <OModalMedia
          {...(resolvedModalMedia as OModalMediaProps)}
          isOpen={isModalOpen}
          portal={true}
          triggerRef={buttonRef}
          onClose={handleModalClose}
        />
      )}
    </>
  );
};

export { TEST_ID as M_VIDEO_THUMBNAIL_TEST_ID };
export { MVIDEOTHUMBNAIL_IMAGE_RENDITION_SPECS };
export * from './MVideoThumbnail.types';
